/**
 * Public unsubscribe landing page — /unsubscribe/:token
 * No authentication required.
 */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Mail, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function UnsubscribeLanding() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | pending | confirmed | error | already | invalid
  const [message, setMessage] = useState("");
  const [brand, setBrand] = useState("Expat Tax Firm");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    axios.get(`${API}/api/unsubscribe/${token}`)
      .then(r => {
        const d = r.data;
        if (d.status === "pending") setState("pending");
        else if (d.status === "already_unsubscribed") setState("already");
        else { setState("invalid"); setMessage(d.message || ""); }
      })
      .catch(() => { setState("error"); setMessage("Unable to process your request. Please try again."); });
  }, [token]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/unsubscribe/${token}`);
      setState("confirmed");
      setMessage(r.data.message || "You have been unsubscribed.");
    } catch (e) {
      setState("error");
      setMessage(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="unsubscribe-page">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-sm w-full text-center">
        <div className="flex justify-center mb-5">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
            <Mail size={22} className="text-slate-600" />
          </div>
        </div>

        <p className="text-xs uppercase tracking-widest text-slate-400 mb-1 font-semibold">{brand}</p>
        <h1 className="text-xl font-bold text-slate-900 mb-2">Email Preferences</h1>

        {state === "loading" && (
          <div className="flex justify-center py-6">
            <Loader2 className="animate-spin text-slate-400" size={28} />
          </div>
        )}

        {state === "pending" && (
          <>
            <p className="text-sm text-slate-600 mb-6" data-testid="unsub-pending-msg">
              Click the button below to unsubscribe from future recovery and follow-up emails from our team.
            </p>
            <button
              data-testid="unsub-confirm-btn"
              onClick={confirm}
              disabled={submitting}
              className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              Unsubscribe me
            </button>
            <p className="text-xs text-slate-400 mt-4">
              You will continue to receive important transactional emails related to any active work.
            </p>
          </>
        )}

        {state === "confirmed" && (
          <div data-testid="unsub-confirmed">
            <CheckCircle size={36} className="text-emerald-500 mx-auto mb-3" />
            <p className="text-sm text-slate-700 mb-4">{message || "You have been successfully unsubscribed."}</p>
            <p className="text-xs text-slate-400">
              You will no longer receive marketing or recovery emails from {brand}.
              To re-subscribe, please contact our team directly.
            </p>
          </div>
        )}

        {state === "already" && (
          <div data-testid="unsub-already">
            <CheckCircle size={36} className="text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600">You have already been unsubscribed. No further action is needed.</p>
          </div>
        )}

        {(state === "error" || state === "invalid") && (
          <div data-testid="unsub-error">
            <XCircle size={36} className="text-rose-500 mx-auto mb-3" />
            <p className="text-sm text-slate-600 mb-4">
              {message || "This unsubscribe link is invalid or has expired."}
            </p>
            <p className="text-xs text-slate-400">
              To update your email preferences, please reply to any of our emails or contact us directly.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
